// Top-level layout for authenticated admin pages: dark app bar with the
// horizontal nav (Users / Pending Invoices) on the left and the logged-in
// admin email + logout on the right. Child routes render inside <Outlet />.
import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Button, Container, Avatar,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';

import { clearSession, getStoredEmail } from '../utils/auth';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// "Pending Invoices" used to be its own top-level tab; it lives as a widget on
// the Dashboard (Phase D) instead, with deep-link to the full /invoices/pending
// list. Other dashboard widgets (pending verifications, user chatbots, chatbot
// history) link to their own detail pages that are NOT in the top nav.
const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon fontSize="small" /> },
    { to: '/users', label: 'Users', icon: <PeopleAltIcon fontSize="small" /> },
    { to: '/plans', label: 'Plans', icon: <WorkspacesIcon fontSize="small" /> },
    { to: '/analytics', label: 'Analytics', icon: <InsightsIcon fontSize="small" /> },
];

const NavTab = ({ to, label, icon }) => (
    <NavLink
        to={to}
        style={({ isActive }) => ({
            textDecoration: 'none',
            color: isActive ? '#ffffff' : 'rgba(255,255,255,0.7)',
            fontFamily,
            fontWeight: 600,
            fontSize: '0.92rem',
            padding: '8px 14px',
            borderRadius: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            backgroundColor: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
            transition: 'background-color 120ms ease',
        })}
    >
        {icon}{label}
    </NavLink>
);

const AppShell = () => {
    const navigate = useNavigate();
    const email = getStoredEmail() || 'super_admin';

    const handleLogout = () => {
        clearSession();
        navigate('/login', { replace: true });
    };

    return (
        <Box sx={{ minHeight: '100vh', fontFamily, backgroundColor: '#f8fafc' }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>
            <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#0f172a' }}>
                <Toolbar sx={{ minHeight: 64, gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ bgcolor: '#3b82f6', width: 32, height: 32, fontFamily, fontWeight: 700 }}>K</Avatar>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.1 }}>
                                Klonit
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.1 }}>
                                Super Admin
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                        {navItems.map((it) => (
                            <NavTab key={it.to} {...it} />
                        ))}
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
                            {email}
                        </Typography>
                        <Button
                            size="small" color="inherit" onClick={handleLogout}
                            startIcon={<LogoutIcon fontSize="small" />}
                            sx={{ fontFamily, textTransform: 'none', color: 'rgba(255,255,255,0.85)' }}
                        >
                            Log out
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Outlet />
            </Container>
        </Box>
    );
};

export default AppShell;
